import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;

/**
 * TaxBreakdownTable
 * - Reads `place_of_supply_is_intra_state` from the snapshot.
 * - Intra-state → CGST + SGST columns (rate %, amount, amount).
 * - Inter-state → IGST column (rate %, amount).
 * - Aggregates per tax_rate_percent across the supplied lines.
 */
export function TaxBreakdownTable({ doc, lines, testid = "tax-breakdown-table" }) {
    const intra = !!doc?.place_of_supply_is_intra_state;
    const safeLines = lines || [];
    const byRate = new Map();
    for (const l of safeLines) {
        const rate = Number(l.tax_rate_percent || 0);
        const row = byRate.get(rate) || { rate, taxable: 0, cgst: 0, sgst: 0, igst: 0 };
        row.taxable += Number(l.taxable_amount_minor || 0);
        row.cgst += Number(l.cgst_amount_minor || 0);
        row.sgst += Number(l.sgst_amount_minor || 0);
        row.igst += Number(l.igst_amount_minor || 0);
        byRate.set(rate, row);
    }
    const rows = Array.from(byRate.values()).sort((a, b) => a.rate - b.rate);

    return (
        <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800" data-testid={testid}>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>GST slab</TableHead>
                        <TableHead className="text-right">Taxable</TableHead>
                        {intra ? (
                            <>
                                <TableHead className="text-right" data-testid="tax-col-cgst">CGST</TableHead>
                                <TableHead className="text-right" data-testid="tax-col-sgst">SGST</TableHead>
                            </>
                        ) : (
                            <TableHead className="text-right" data-testid="tax-col-igst">IGST</TableHead>
                        )}
                        <TableHead className="text-right">Total tax</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={intra ? 5 : 4} className="py-6 text-center text-sm text-zinc-500">No taxable lines.</TableCell>
                        </TableRow>
                    )}
                    {rows.map((r) => (
                        <TableRow key={r.rate} data-testid={`tax-row-${r.rate}`}>
                            <TableCell className="font-mono">{r.rate}%</TableCell>
                            <TableCell className="text-right font-mono">{inr(r.taxable)}</TableCell>
                            {intra ? (
                                <>
                                    <TableCell className="text-right font-mono" data-testid={`tax-cgst-${r.rate}`}>
                                        {inr(r.cgst)} <span className="text-[10px] text-zinc-500">@ {r.rate / 2}%</span>
                                    </TableCell>
                                    <TableCell className="text-right font-mono" data-testid={`tax-sgst-${r.rate}`}>
                                        {inr(r.sgst)} <span className="text-[10px] text-zinc-500">@ {r.rate / 2}%</span>
                                    </TableCell>
                                </>
                            ) : (
                                <TableCell className="text-right font-mono" data-testid={`tax-igst-${r.rate}`}>
                                    {inr(r.igst)} <span className="text-[10px] text-zinc-500">@ {r.rate}%</span>
                                </TableCell>
                            )}
                            <TableCell className="text-right font-mono">
                                {inr(intra ? (r.cgst + r.sgst) : r.igst)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className="border-t border-zinc-200 px-4 py-2 text-xs font-mono text-zinc-500 dark:border-zinc-800">
                {intra
                    ? "Intra-state supply · CGST + SGST applied"
                    : "Inter-state supply · IGST applied"}
                {doc?.place_of_supply_state_code && (
                    <> · place of supply <span className="font-bold text-zinc-700 dark:text-zinc-300">{doc.place_of_supply_state_code}</span></>
                )}
            </div>
        </div>
    );
}
